/*eslint no-underscore-dangle: 0 */

import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import range from 'lodash/range';

import {
  ARTIFICIAL_ENDING_ID,
  generateNewStep,
  generateStepId,
  hasFinalEnding,
} from 'app/procedures/utils';
import {gt} from 'app/utils/i18n';

import {DocumentLanesRegistryType, LaneSerializedType} from 'sweet-types';

import type {
  NewlyAttachedEmbedType,
  NewlyAttachedImageOrVideoType,
} from 'app/general/types';
import type {NewStepStructureParams} from 'app/procedures/types';
import type {
  AnyStepType,
  AttachedFile,
  DecisionStepChoiceType,
  ProcedureSerializedType,
  ProcessProcedureAsStepSerializedType,
  StepFieldType,
} from 'sweet-types';
import type {AutomationType} from 'sweet-types/procedure/procedure-step';

type GenericApplyShaperFuncType<T> = (
  mutate: (shaper: T, setEdit: (id: string | false) => any) => DocumentShaper,
  immediate?: boolean
) => any;

export type ApplyDocumentShaperFuncType =
  GenericApplyShaperFuncType<DocumentShaper>;

export type ApplyStepShaperFuncType = GenericApplyShaperFuncType<StepShaper>;

export type ApplyStepChoicesFuncType =
  GenericApplyShaperFuncType<StepChoicesShaper>;

export type DeletableAnyStepType = AnyStepType & {delete?: boolean};

interface DocWithStepsType {
  content: Array<DeletableAnyStepType>;
  lanes: DocumentLanesRegistryType;
}

export type DocFragment = Partial<DocWithStepsType>;

export type TargetStepIdType =
  | null
  | string
  | {
      type: 'newStep';
      title: string;
    };

export class DocumentShaper {
  document: DocWithStepsType;

  // a map of touched steps
  // required to inform the process page which updated steps should be sent to backend
  touchedSteps: Record<string, boolean>;
  mostRecentAddedStepId: null | string;

  constructor(
    document: DocWithStepsType,
    touchedSteps: Record<string, boolean> = {}
  ) {
    if (!(this instanceof DocumentShaper)) {
      return new DocumentShaper(document);
    }

    this.document = document;
    this.touchedSteps = touchedSteps;
    this.mostRecentAddedStepId = null;
  }

  new(
    data: DocFragment,
    touchedSteps: Record<string, boolean> = {}
  ): DocumentShaper {
    return new DocumentShaper({...this.document, ...data}, touchedSteps);
  }

  val(): DocWithStepsType {
    return this.document;
  }

  getTouchedSteps(): Array<string> {
    return Object.keys(this.touchedSteps);
  }

  /* return the procedure steps to be read */
  getSteps(): Array<AnyStepType> {
    return this.val().content;
  }

  getLanes(): DocumentLanesRegistryType {
    return this.val().lanes;
  }

  updateLanes(lanes: DocumentLanesRegistryType): DocumentShaper {
    return this.new({lanes}, this.touchedSteps);
  }

  /*
    run an external func and use the returned value to continue the chain
    Its useful in situations you need to do some conditional check inside the chain
    */
  run(func: (shaper: DocumentShaper) => DocumentShaper): DocumentShaper {
    return func(this);
  }

  /* auxiliary func to execute some side effect func and then continue the chain */
  runSideEffect(func: (shaper: DocumentShaper) => any): DocumentShaper {
    func(this);
    return this;
  }

  genStepId(): string {
    return generateStepId();
  }

  addTouchedStep(id: string) {
    this.touchedSteps[id] = true;
  }

  updateStepsFunc(
    updater: (
      steps: Array<DeletableAnyStepType>,
      id: (id: string) => void
    ) => Array<DeletableAnyStepType>
  ): DocumentShaper {
    let i = 0;
    return this.new(
      {
        content: updater(this.document.content, (id) => this.addTouchedStep(id)) // auto adjust all steps n values
          // deleted steps don't increase n
          .map((step) =>
            clone(step, {
              // @ts-expect-error this number + boolean magic actually works.
              n: i + step.delete ? i : ++i,
            })
          ),
      },
      this.touchedSteps
    );
  }

  createStepStructure(
    type: 'content' | 'decision' | 'automation' | 'ending',
    {after, title = '', content = {}}: NewStepStructureParams = {}
  ): StepShaper {
    const newStep = generateNewStep(type, title, content);
    this.addTouchedStep(newStep.id);
    this.mostRecentAddedStepId = newStep.id;
    return this.updateStepsFunc((steps) => {
      const position = getStepPosition(after, steps, newStep.type);
      return [...steps.slice(0, position), newStep, ...steps.slice(position)];
    }).step(newStep.id);
  }

  createContentStep = (opts: NewStepStructureParams = {}): StepShaper =>
    this.createStepStructure('content', opts);

  // eslint-disable-next-line no-underscore-dangle
  _convertProcedureToStep(
    procedure: ProcedureSerializedType
  ): ProcessProcedureAsStepSerializedType {
    return {
      type: 'processprocedure',
      id: procedure.hashid,
      hashid: procedure.hashid,
      url: procedure.url,
      html_url: procedure.html_url,
      name: procedure.name,
      can_edit: procedure.can_edit,
      can_change: procedure.can_change,
      can_view: true,
      private: procedure.private,
      current_version: procedure.current_version,
      edited_at: procedure.edited_at,
      approved_at: procedure.approved_at,
      title: procedure.name,
      procedure_id: procedure.id,
      embed_url: procedure.embed_url,
      content_type: procedure.content_type,
      n: 0, // it will be overwritten in updateStepsFunc
    };
  }

  createProcedureStep = (
    procedure: ProcedureSerializedType,
    after?: string
  ): StepShaper => {
    const newStep: ProcessProcedureAsStepSerializedType =
      this._convertProcedureToStep(procedure);

    this.addTouchedStep(newStep.id);
    this.mostRecentAddedStepId = newStep.id;
    return this.updateStepsFunc((steps) => {
      const position = getStepPosition(after, steps);
      return [...steps.slice(0, position), newStep, ...steps.slice(position)];
    }).step(newStep.id);
  };

  createDecisionStep = (opts: NewStepStructureParams = {}): StepShaper =>
    this.createStepStructure('decision', opts)
      .choices.addBlank()
      .choices.addBlank();

  createAutomationStep = (opts: NewStepStructureParams = {}): StepShaper =>
    this.createStepStructure('automation', opts);

  createEndingStep = (opts: NewStepStructureParams = {}): StepShaper =>
    this.createStepStructure('ending', {
      title: gt('End'),
      ...opts,
    });

  moveStep = (sourceIndex: number, targetIndex: number): DocumentShaper => {
    return this.updateStepsFunc((steps, addTouched) => {
      // mark steps between source and target indexes as touched
      // so they are sent to the backend as well with their new position
      const [start, end] = [sourceIndex, targetIndex].sort();
      range(start, end + 1).forEach((i) => addTouched(steps[i].id));
      return moveInList(steps, sourceIndex, targetIndex);
    });
  };

  moveStepById = (sourceId: string, targetId: string): DocumentShaper => {
    const sourceIndex = findIndex(this.getSteps(), {
      id: sourceId,
    });
    const targetIndex = findIndex(this.getSteps(), {
      id: targetId,
    });
    return this.moveStep(sourceIndex, targetIndex);
  };

  _ensureTargetIsValid(
    target: TargetStepIdType
  ): [DocumentShaper, string | undefined] {
    if (target === ARTIFICIAL_ENDING_ID) {
      const stepShaper = this.createEndingStep();
      return [stepShaper.procedure(), stepShaper.getId()];
    }

    if (target != null && typeof target !== 'string') {
      const stepShaper = this.createContentStep({
        title: target.title,
      });
      return [stepShaper.procedure(), stepShaper.getId()];
    }

    return [this, target != null ? target : undefined];
  }

  step(stepId: string): StepShaper {
    return new StepShaper(stepId, this);
  }
}
export class StepShaper {
  stepId: string;
  documentShaper: DocumentShaper;

  constructor(stepId: string, documentShaper: DocumentShaper) {
    this.stepId = stepId;
    this.documentShaper = documentShaper;
  }

  procedure() {
    return this.documentShaper;
  }

  val() {
    return this.documentShaper.val();
  }

  /* return current step data to be read - not updated */
  getStepData(): DeletableAnyStepType | void {
    return find(this.procedure().getSteps(), {
      id: this.stepId,
    });
  }

  /*
    run an external func and use the returned value to continue the chain
    Its useful in situations you need to do some conditional inside the chain
    */
  run(
    func: (shaper: StepShaper, setTouched: (arg0: string) => void) => StepShaper
  ): StepShaper {
    return func(this, this.setTouched);
  }

  setTouched() {
    this.procedure().addTouchedStep(this.getId());
  }

  /* auxiliary func to execute some side effect func and then continue the chain */
  runSideEffect(
    func: (arg0: StepShaper, arg1: (arg0: string) => void) => any
  ): StepShaper {
    func(this, this.setTouched);
    return this;
  }

  new(procedureData: DocFragment): StepShaper {
    return new StepShaper(
      this.stepId,
      this.documentShaper.new(procedureData, this.documentShaper.touchedSteps)
    );
  }

  getId() {
    return this.stepId;
  }

  getField(fieldName: string): any {
    const step = this.getStepData();

    if (step == null) {
      throw Error(`Step ${this.getId()} not found`);
    }

    return step[fieldName];
  }

  update(update: Record<string, any>): StepShaper {
    this.setTouched();
    return this.updateFunc((step) => clone(step, update));
  }

  updateFunc(
    update: (step: DeletableAnyStepType) => DeletableAnyStepType
  ): StepShaper {
    this.setTouched();
    return this.new({
      content: updateListItem<DeletableAnyStepType>(
        this.documentShaper.document.content,
        this.stepId,
        update
      ),
    });
  }

  setNext(nextId: TargetStepIdType): StepShaper {
    let [shaper, next] = this.procedure()._ensureTargetIsValid(nextId);

    let prevStepId: string;
    // unset next value if next is pointing to the next step
    shaper.updateStepsFunc((steps) => {
      steps.forEach((step) => {
        if (next != null && step.id === next && this.getId() === prevStepId) {
          next = undefined;
        }

        prevStepId = step.id;
      });
      return steps;
    });
    return shaper.step(this.stepId).update({
      next,
    });
  }

  setLane(stepLane: LaneSerializedType | null): StepShaper {
    return this.procedure()
      .run((docShaper) => {
        const lanes = {...docShaper.getLanes()};

        if (stepLane == null) {
          delete lanes[this.stepId];
        } else {
          lanes[this.stepId] = stepLane.hashid;
        }

        return docShaper.updateLanes(lanes);
      })
      .step(this.stepId);
  }

  remove({
    replaceWith,
  }: {
    replaceWith?: DeletableAnyStepType;
  } = {}): DocumentShaper {
    let newStep = replaceWith;
    const oldStepId = this.stepId;

    return this.procedure().updateStepsFunc((steps, addTouched) => {
      let updatedSteps = steps.map((rawStep) => {
        let step = clone(rawStep);

        if (step.id === oldStepId) {
          addTouched(step.id);
          step.delete = true;
        }

        if (step.type === 'decision') {
          step.choices = (step.choices || []).reduce((choices, choice) => {
            if (choice.dest !== oldStepId) {
              choices.push(choice);
              return choices;
            } else {
              if (newStep) {
                choice.dest = newStep.id;
                choices.push(choice);
              }

              addTouched(step.id);
              return choices;
            }
          }, [] as DecisionStepChoiceType[]);
        }

        if (
          step.type !== 'decision' &&
          step.type !== 'ending' &&
          step.type !== 'automation' &&
          step.next === oldStepId
        ) {
          addTouched(step.id);

          if (newStep != null) {
            step.next = newStep.id;
          } else {
            step.next = this.procedure().step(oldStepId).getNextStepId();
          }
        }

        return step;
      });

      if (newStep != null) {
        addTouched(newStep.id);
        // subtract 1 to put the new step before the step being deleted
        // so the backend can keep the new in the old one place
        const position = getStepPosition(oldStepId, updatedSteps) - 1;
        updatedSteps = [
          ...updatedSteps.slice(0, position),
          newStep,
          ...updatedSteps.slice(position),
        ];
      }

      return updatedSteps;
    });
  }

  convertToContentType = (): StepShaper =>
    this.update({
      type: 'content',
      choices: undefined,
      fields: undefined,
      images: [],
      files: [],
      embeds: [],
    });
  convertToDecisionType = (): StepShaper =>
    this.update({
      type: 'decision',
      fields: undefined,
      images: [],
      files: [],
      embeds: [],
      next: undefined,
    })
      .choices.addBlank()
      .choices.addBlank();
  convertToEndingType = (): StepShaper =>
    this.update({
      type: 'ending',
      choices: undefined,
      fields: undefined,
      images: [],
      files: [],
      embeds: [],
    });

  clone(): StepShaper {
    const cloneId = this.procedure().genStepId();
    return this.procedure()
      .updateStepsFunc((steps, addTouched) => {
        let newSteps = [] as Array<DeletableAnyStepType>;
        addTouched(cloneId);
        steps.forEach((step) => {
          newSteps.push(step);

          if (step.id === this.stepId) {
            newSteps.push(
              clone(step, {
                id: cloneId,
              })
            );
          }
        });
        return newSteps;
      })
      .step(cloneId)
      .fields.updateItemsFunc((fields) =>
        fields.map((field) =>
          clone(field, {
            id: this.fields.genId(),
          })
        )
      )
      .choices.updateItemsFunc((choices) => {
        const existingChoices = this.getField('choices');

        // doesnt add choices for a step which doesn't have it
        // otherwise backend will convert the step to decision type
        if (existingChoices == null) {
          return null;
        }

        return choices.map((choice) =>
          clone(choice, {
            id: this.choices.genId(),
          })
        );
      })
      .procedure()
      .step(cloneId);
  }

  getNextStepId(avoidIds: Array<string> = []): string {
    const next = this.getField('next');

    if (next != null) {
      return next;
    }

    const steps = this.procedure().getSteps();
    const currentStepIndex = findIndex(steps, {
      id: this.stepId,
    });

    for (let i = currentStepIndex + 1; i < steps.length; i++) {
      const step = steps[i];

      if (avoidIds.indexOf(step.id) === -1) {
        return step.id;
      }
    }

    return ARTIFICIAL_ENDING_ID;
  }

  choices = new StepChoicesShaper(this);
  images = new StepImagesShaper(this);
  embeds = new StepEmbedsShaper(this);
  files = new StepFilesShaper(this);
  fields = new StepFieldsShaper(this);
  automation = new StepAutomationShaper(this);
}
export class StepAttachedItemsShaper<T extends Record<string, any>> {
  attr: string;
  stepShaper: StepShaper;

  constructor(stepShaper: StepShaper) {
    this.stepShaper = stepShaper;
  }

  step() {
    return this.stepShaper;
  }

  val() {
    return this.stepShaper.val();
  }

  genId(): string {
    return generateStepId();
  }

  updateItemsFunc(update: (arg0: Array<T>) => Array<T> | null): StepShaper {
    const attr = this.attr;
    return this.step().updateFunc((step) =>
      clone(step, {
        [attr]: update(step[attr] || []),
      })
    );
  }

  forEach(iterator: (arg0: T) => any): StepShaper {
    return this.step().updateFunc((step) => {
      step[this.attr].forEach(iterator);
      return step;
    });
  }

  add(item: T): StepShaper {
    return this.updateItemsFunc((items) => [...items, item]);
  }

  remove(itemId: string): StepShaper {
    return this.updateItemsFunc((items) =>
      items.filter((item) => item.id !== itemId)
    );
  }

  removeByIndex(index: number): StepShaper {
    return this.updateItemsFunc((items) =>
      items.filter((item, i) => i !== index)
    );
  }

  updateFunc(itemId: string, updater: (arg0: T) => T): StepShaper {
    return this.updateItemsFunc((items) =>
      updateListItem<T>(items, itemId, updater)
    );
  }

  updateByIndexFunc(index: number, updater: (arg0: T) => T): StepShaper {
    return this.updateItemsFunc((items) =>
      updateListItem<T>(items, index, updater, 'index')
    );
  }

  update(itemId: string, update: Partial<T>): StepShaper {
    return this.updateFunc(itemId, (item) => clone(item, update));
  }

  updateByIndex(index: number, update: Partial<T>): StepShaper {
    return this.updateByIndexFunc(index, (item) => clone(item, update));
  }

  move = (sourceIndex: number, targetIndex: number): StepShaper => {
    return this.updateItemsFunc((items) =>
      moveInList(items, sourceIndex, targetIndex)
    );
  };
}
export class StepChoicesShaper extends StepAttachedItemsShaper<DecisionStepChoiceType> {
  attr = 'choices';

  addBlank(opts: Partial<DecisionStepChoiceType> = {}): StepShaper {
    return this.add({
      name: '',
      dest: '',
      ...opts,
      id: this.genId(),
    });
  }

  setTarget(choiceId: string, targetId: TargetStepIdType): StepShaper {
    const [documentShaper, dest] = this.step()
      .procedure()
      ._ensureTargetIsValid(targetId);

    return documentShaper
      .step(this.step().stepId)
      .choices.update(choiceId, {
        dest,
      })
      .choices.adjustChoicesNexts();
  }

  /* this make sure all choices of a decision step are not connected */
  adjustChoicesNexts(): StepShaper {
    const shaper = this.step();
    const choices: Array<DecisionStepChoiceType> =
      shaper.getField('choices') || [];

    if (choices.length === 0) {
      return shaper;
    }

    /* map all existing steps ids */
    const stepsMap = shaper
      .procedure()
      .getSteps()
      .reduce((map, {id}) => ({...map, [id]: true}), {});
    const newTargetId = shaper.getNextStepId(choices.map((c) => c.dest));
    const choicesTargets = choices.map((c) => c.dest);
    return choices.reduce((shaper, choice) => {
      /* make sure dest is an existing step */
      if (!choice.dest || stepsMap[choice.dest] == null) {
        return shaper;
      }

      const destShaper = shaper.procedure().step(choice.dest);
      const next = destShaper.getField('next');
      const stepType = destShaper.getField('type');

      if (
        stepType === 'content' &&
        next == null &&
        choicesTargets.indexOf(destShaper.getNextStepId()) > -1
      ) {
        shaper = shaper.procedure().step(choice.dest).setNext(newTargetId);
      }

      return shaper;
    }, shaper);
  }
}

class StepImagesShaper extends StepAttachedItemsShaper<NewlyAttachedImageOrVideoType> {
  attr = 'images';
}
class StepEmbedsShaper extends StepAttachedItemsShaper<NewlyAttachedEmbedType> {
  attr = 'embeds';
}

class StepFilesShaper extends StepAttachedItemsShaper<AttachedFile> {
  attr = 'files';
}

class StepFieldsShaper extends StepAttachedItemsShaper<StepFieldType> {
  attr = 'fields';
}
class StepAutomationShaper extends StepAttachedItemsShaper<AutomationType> {
  attr = 'automation';
}

function clone<T>(obj: T, attrs: Partial<T> = {}): T {
  return {...obj, ...attrs};
}

function moveInList<T>(
  list: Array<T>,
  sourceIndex: number,
  targetIndex: number
): Array<T> {
  const newList = [...list];
  newList.splice(sourceIndex, 1);
  newList.splice(targetIndex, 0, list[sourceIndex]);
  return newList;
}

function updateListItem<T extends Record<string, any>>(
  list: Array<T> | void,
  id: number | string,
  updater: (item: T, index: number) => T,
  findBy: 'id' | 'index' = 'id'
): Array<T> {
  return (list || [])
    .map<T>((item, index) =>
      (findBy === 'id' && item.id === id) ||
      (findBy === 'index' && index === id)
        ? updater(clone(item), index)
        : item
    )
    .filter((item) => item != null);
}

function getStepPosition(
  after: string | undefined,
  steps: Array<DeletableAnyStepType>,
  stepType?: AnyStepType['type']
): number {
  return after == null
    ? steps.length - (hasFinalEnding(steps) && stepType !== 'ending' ? 1 : 0)
    : after === 'start'
    ? 0
    : findIndex(steps, {
        id: after,
      }) + 1;
}
